/**
 * Pure model + validator for the Hand Analyzer's BuiltHand.
 *
 * The user constructs a complete hand they played, then launches the coach to
 * grade the line. Building/saving is free; the coach grade is the paid product.
 *
 * `validateBuiltHand` mirrors the backend's invariants EXACTLY — the Save CTA
 * is only enabled when this returns []. If the two drift, saves 422 server-side
 * (or, worse, a save succeeds on a hand the user couldn't fix client-side).
 *
 * Key gotcha: action `amount` is the ABSOLUTE bet LEVEL for the street
 * ("raise to 9bb" → amount 9), required + positive for `bet`/`raise`. Blinds
 * are implicit — no "post" actions.
 */

import type {
  BuiltHand,
  BuiltHandAction,
  HandEndedOn,
} from "@/shared/models";
import { isCanonicalPosition } from "@/features/chat/lib/seatLayout";
import { streetState, type StreetKey } from "./handTableEngine";

const RANKS = "23456789TJQKA";
const SUITS = "shdc";

/** All 52 wire tokens, rank-major then suit (e.g. "2s","2h",…,"Ac"). */
export const FULL_DECK: string[] = (() => {
  const deck: string[] = [];
  for (const r of RANKS) {
    for (const s of SUITS) {
      deck.push(`${r}${s}`);
    }
  }
  return deck;
})();

const DECK_SET: ReadonlySet<string> = new Set(FULL_DECK);

/** True if `token` is a valid 2-char wire card (rank in 23456789TJQKA, suit in shdc). */
export function isValidCard(token: string | null | undefined): token is string {
  return typeof token === "string" && DECK_SET.has(token);
}

/** A sensible blank hand: hero BTN, no cards, 100bb, one unknown BB villain,
 *  preflop only, ended preflop. */
export function emptyHand(): BuiltHand {
  return {
    heroPosition: "BTN",
    heroCards: ["", ""],
    effectiveStackBb: 100,
    villains: [{ position: "BB", cards: null }],
    streets: { preflop: { actions: [] } },
    endedOn: "preflop",
  };
}

/** Every card token currently placed anywhere (hero, villains shown, board).
 *  Invalid/empty slots are skipped — used for dup detection + picker disabling. */
export function usedCards(hand: BuiltHand): Set<string> {
  const used = new Set<string>();
  const add = (c: string | null | undefined) => {
    if (isValidCard(c)) used.add(c);
  };
  hand.heroCards.forEach(add);
  for (const v of hand.villains) {
    if (v.cards) v.cards.forEach(add);
  }
  const { flop, turn, river } = hand.streets;
  if (flop) flop.board.forEach(add);
  if (turn) add(turn.card);
  if (river) add(river.card);
  return used;
}

/** Cards a picker should disable for one slot: every used card EXCEPT the one
 *  the slot already holds (so re-picking its own value stays allowed). Shared by
 *  the seat hole-card slots and the board slots. */
export function cardsDisabledFor(
  used: Set<string>,
  current: string | null,
): Set<string> {
  if (!current) return used;
  const set = new Set(used);
  set.delete(current);
  return set;
}

/**
 * The streets the entered ACTION has opened, in order — the action drives the
 * reveal (not a board count). A street becomes reachable only when the prior
 * street's betting round CLOSED with ≥2 players still live to see it:
 *
 *   - preflop  — always (the felt always has hero + ≥1 villain).
 *   - flop     — preflop closed AND ≥2 remain.
 *   - turn     — flop has its 3 board cards AND flop closed AND ≥2 remain.
 *   - river    — turn has its card AND turn closed AND ≥2 remain.
 *
 * Postflop streets gate on their OWN board being complete before the next can
 * open (you can't have a turn round resolve before the flop is on the table).
 * `streetState` tolerates a missing street object (reads `?.actions ?? []`), so
 * this is safe to call before the street objects exist — that's how the builder
 * decides which street objects to create.
 */
export function reachableStreets(hand: BuiltHand): StreetKey[] {
  const reachable: StreetKey[] = ["preflop"];

  const pre = streetState(hand, "preflop");
  if (!(pre.closed && pre.remaining.length >= 2)) return reachable;
  reachable.push("flop");

  const flopBoardComplete =
    (hand.streets.flop?.board.filter(isValidCard).length ?? 0) === 3;
  if (!flopBoardComplete) return reachable;
  const flop = streetState(hand, "flop");
  if (!(flop.closed && flop.remaining.length >= 2)) return reachable;
  reachable.push("turn");

  const turnCardComplete = isValidCard(hand.streets.turn?.card);
  if (!turnCardComplete) return reachable;
  const turn = streetState(hand, "turn");
  if (!(turn.closed && turn.remaining.length >= 2)) return reachable;
  reachable.push("river");

  return reachable;
}

/** The streets actually present, in order — the legal `endedOn` targets
 *  (plus "showdown" when a river is present). */
export function reachedStreets(hand: BuiltHand): HandEndedOn[] {
  const reached: HandEndedOn[] = ["preflop"];
  if (hand.streets.flop) reached.push("flop");
  if (hand.streets.turn) reached.push("turn");
  if (hand.streets.river) reached.push("river");
  if (hand.streets.river) reached.push("showdown");
  return reached;
}

/** Actors valid for a given street's action rows: "Hero" + the villain seats. */
export function actorOptions(hand: BuiltHand): string[] {
  return ["Hero", ...hand.villains.map((v) => v.position)];
}

const ACTIONS_NEEDING_AMOUNT = new Set(["bet", "raise"]);

/**
 * Validate against the backend invariants. Returns human-readable error
 * strings; [] means the hand is savable. Each string is meant to be shown
 * inline under the builder.
 */
export function validateBuiltHand(hand: BuiltHand): string[] {
  const errors: string[] = [];

  // Hero seat
  if (!isCanonicalPosition(hand.heroPosition)) {
    errors.push("Pick a seat for Hero.");
  }

  // Hero cards — exactly 2 valid
  const heroValid = hand.heroCards.filter(isValidCard);
  if (heroValid.length !== 2) {
    errors.push("Hero needs both hole cards.");
  }

  // Effective stack
  if (!(hand.effectiveStackBb > 0)) {
    errors.push("Effective stack must be greater than 0bb.");
  }

  // Villains — at least one, distinct canonical seats, none equal to hero
  if (hand.villains.length < 1) {
    errors.push("Add at least one villain.");
  }
  const seenSeats = new Set<string>();
  for (const v of hand.villains) {
    if (!isCanonicalPosition(v.position)) {
      errors.push("Every villain needs a seat.");
      continue;
    }
    if (v.position === hand.heroPosition) {
      errors.push(`A villain can't sit in Hero's seat (${v.position}).`);
    }
    if (seenSeats.has(v.position)) {
      errors.push(`Two villains are in the same seat (${v.position}).`);
    }
    seenSeats.add(v.position);
    // Villain cards: null OR exactly 2 valid
    if (v.cards !== null) {
      const vValid = v.cards.filter(isValidCard);
      if (vValid.length !== 2) {
        errors.push(`${v.position}'s shown cards must be both filled (or left unknown).`);
      }
    }
  }

  // Board shape: flop = 3 cards
  const { flop, turn, river } = hand.streets;
  if (flop) {
    const flopValid = flop.board.filter(isValidCard);
    if (flopValid.length !== 3) {
      errors.push("The flop needs 3 cards.");
    }
  }
  if (turn && !isValidCard(turn.card)) {
    errors.push("The turn needs a card.");
  }
  if (river && !isValidCard(river.card)) {
    errors.push("The river needs a card.");
  }

  // Street contiguity: turn requires flop, river requires turn
  if (turn && !flop) {
    errors.push("Add the flop before the turn.");
  }
  if (river && !turn) {
    errors.push("Add the turn before the river.");
  }

  // Global card uniqueness (no dupes anywhere)
  const allCards: string[] = [];
  hand.heroCards.forEach((c) => isValidCard(c) && allCards.push(c));
  for (const v of hand.villains) {
    if (v.cards) v.cards.forEach((c) => isValidCard(c) && allCards.push(c));
  }
  if (flop) flop.board.forEach((c) => isValidCard(c) && allCards.push(c));
  if (turn && isValidCard(turn.card)) allCards.push(turn.card);
  if (river && isValidCard(river.card)) allCards.push(river.card);
  const dupes = new Set<string>();
  const seenCards = new Set<string>();
  for (const c of allCards) {
    if (seenCards.has(c)) dupes.add(c);
    seenCards.add(c);
  }
  if (dupes.size > 0) {
    errors.push(`Each card can only appear once — duplicate: ${[...dupes].join(", ")}.`);
  }

  // Action amounts: bet/raise carry a positive amount
  const allActions: BuiltHandAction[] = [
    ...hand.streets.preflop.actions,
    ...(flop?.actions ?? []),
    ...(turn?.actions ?? []),
    ...(river?.actions ?? []),
  ];
  // Each action's actor must be "Hero" or a current villain seat — the backend
  // (Pydantic BuiltHand) rejects anything else with a 422, so mirror it here or
  // Save enables on a hand the server refuses. See CLAUDE.md gotcha.
  const validActors = new Set(actorOptions(hand));
  for (const a of allActions) {
    if (!validActors.has(a.actor)) {
      errors.push(
        `Unknown actor "${a.actor}" in the action — must be Hero or a villain seat.`,
      );
    }
    if (ACTIONS_NEEDING_AMOUNT.has(a.action)) {
      if (!(typeof a.amount === "number" && a.amount > 0)) {
        errors.push(`A ${a.action} by ${a.actor} needs a positive amount (bb).`);
      }
    }
  }

  // endedOn must be a street that was actually reached; showdown requires river
  const reached = reachedStreets(hand);
  if (!reached.includes(hand.endedOn)) {
    if (hand.endedOn === "showdown") {
      errors.push("Showdown requires a river.");
    } else {
      errors.push(`The hand can't end on the ${hand.endedOn} — that street wasn't reached.`);
    }
  }

  return errors;
}

/**
 * Derive where the hand ended from the entered action — the value the user no
 * longer picks by hand (the table model infers it). Rules:
 *
 *   1. If a street's action leaves exactly ONE player live (everyone else
 *      folded), the hand ends on THAT street, no showdown.
 *   2. Otherwise the hand reaches the deepest street that has board/action.
 *      If that deepest street's betting round CLOSED with ≥2 players still in,
 *      it's a `"showdown"`; if the round is still open (incomplete entry) it
 *      ends on that street.
 *
 * Pure + total: every BuiltHand maps to a legal `endedOn`. Used to keep
 * `hand.endedOn` in sync as the user enters action, so the validator's
 * "endedOn is a reached street" invariant always holds.
 */
export function deriveEndedOn(hand: BuiltHand): HandEndedOn {
  const order: { key: StreetKey; ended: HandEndedOn }[] = [
    { key: "preflop", ended: "preflop" },
    { key: "flop", ended: "flop" },
    { key: "turn", ended: "turn" },
    { key: "river", ended: "river" },
  ];
  const present = order.filter(({ key }) =>
    key === "preflop" ? true : !!hand.streets[key],
  );

  // Rule 1: a fold that leaves a single player live ends the hand on that street.
  for (const { key, ended } of present) {
    const state = streetState(hand, key);
    if (state.foldedThisStreet.size > 0 && state.remaining.length <= 1) {
      return ended;
    }
  }

  // Rule 2: deepest reached street; showdown when its round closed with ≥2 in.
  const deepest = present[present.length - 1]!;
  const deepestState = streetState(hand, deepest.key);
  if (
    deepest.key === "river" &&
    deepestState.closed &&
    deepestState.remaining.length >= 2
  ) {
    return "showdown";
  }
  return deepest.ended;
}
